import React from 'react';
import type { Block, BlockType } from '../../types';
import { isKnownBlockType } from '../../types';
import { BulletListBlockView } from './BulletListBlockView';
import { NumberedListBlockView } from './NumberedListBlockView';
import { TableBlockView } from './TableBlockView';
import { ParagraphBlockView } from './ParagraphBlockView';
import { CodeBlockView } from './CodeBlockView';
import { MetricBlockView } from './MetricBlockView';
import { ComparisonBlockView } from './ComparisonBlockView';
import { TimelineBlockView } from './TimelineBlockView';
import { ApprovalCardBlockView } from './ApprovalCardBlockView';
import { ChartEmbedBlockView } from './ChartEmbedBlockView';
import { FollowUpChipsBlockView } from './FollowUpChipsBlockView';

export interface BlockRendererProps {
  blocks: Block[];
  onFollowUpSelect?: (suggestion: string) => void;
  onApprovalOptionSelect?: (option: string, hilRequestId?: string) => void;
  /**
   * DR-17 observability hook — invoked once per render pass for each block
   * entry that is unrecognized, missing required fields, or throws while
   * rendering. Callers can wire this to console/telemetry.
   */
  onUnknownBlock?: (block: unknown) => void;
}

/** Renders unknown/malformed block entries as plain text — never throws, never blanks the list (DR-7). */
export function UnknownBlockFallback({ block }: { block: unknown }): React.ReactElement {
  const typeLabel =
    block && typeof block === 'object' && 'type' in block
      ? String((block as { type: unknown }).type)
      : 'unknown';
  return (
    <div data-testid="block-unknown" className="text-text-secondary text-[13px] italic">
      [Unsupported content: {typeLabel}]
    </div>
  );
}

const REQUIRED_FIELDS: Record<BlockType, readonly string[]> = {
  bullet_list: ['items'],
  numbered_list: ['items'],
  table: ['headers', 'rows'],
  paragraph: ['text'],
  code: ['content'],
  metric: ['label', 'value'],
  comparison: ['options'],
  timeline: ['steps'],
  approval_card: ['question', 'options'],
  chart_embed: ['embed_view_url'],
  follow_up_chips: ['suggestions'],
};

function isWellFormedBlock(candidate: unknown): candidate is Block {
  if (!candidate || typeof candidate !== 'object') return false;
  const type = (candidate as { type?: unknown }).type;
  if (!isKnownBlockType(type)) return false;
  return REQUIRED_FIELDS[type].every(key =>
    Object.prototype.hasOwnProperty.call(candidate, key)
  );
}

interface BoundaryProps {
  fallback: React.ReactElement;
  onError: () => void;
  children: React.ReactNode;
}
interface BoundaryState {
  hasError: boolean;
}

/**
 * Per-block error boundary — last-resort safety net for unexpected render
 * errors (e.g. a field shaped differently than expected slipping past
 * isWellFormedBlock). Pairs with the upfront shape check below so both known
 * failure classes (malformed shape, unexpected throw) degrade to the same
 * plain-text fallback instead of blanking the whole message (AC-6).
 */
class BlockErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    console.error('[BlockRenderer] a block failed to render', error);
    this.props.onError();
  }

  render(): React.ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function renderKnownBlock(
  block: Block,
  ctx: {
    onFollowUpSelect?: (suggestion: string) => void;
    onApprovalOptionSelect?: (option: string, hilRequestId?: string) => void;
  }
): React.ReactElement {
  switch (block.type) {
    case 'bullet_list':
      return <BulletListBlockView block={block} />;
    case 'numbered_list':
      return <NumberedListBlockView block={block} />;
    case 'table':
      return <TableBlockView block={block} />;
    case 'paragraph':
      return <ParagraphBlockView block={block} />;
    case 'code':
      return <CodeBlockView block={block} />;
    case 'metric':
      return <MetricBlockView block={block} />;
    case 'comparison':
      return <ComparisonBlockView block={block} />;
    case 'timeline':
      return <TimelineBlockView block={block} />;
    case 'approval_card':
      return <ApprovalCardBlockView block={block} onOptionSelect={ctx.onApprovalOptionSelect} />;
    case 'chart_embed':
      return <ChartEmbedBlockView block={block} />;
    case 'follow_up_chips':
      return <FollowUpChipsBlockView block={block} onSelect={ctx.onFollowUpSelect} />;
    default: {
      // Exhaustiveness guard — unreachable given isWellFormedBlock's isKnownBlockType check.
      const exhaustive: never = block;
      throw new Error(`Unhandled block type: ${JSON.stringify(exhaustive)}`);
    }
  }
}

export function BlockRenderer({
  blocks,
  onFollowUpSelect,
  onApprovalOptionSelect,
  onUnknownBlock,
}: BlockRendererProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-2.5" data-testid="block-renderer">
      {blocks.map((block, index) => {
        const fallback = <UnknownBlockFallback block={block} />;

        if (!isWellFormedBlock(block)) {
          onUnknownBlock?.(block);
          return <React.Fragment key={index}>{fallback}</React.Fragment>;
        }

        return (
          <BlockErrorBoundary
            key={index}
            fallback={fallback}
            onError={() => onUnknownBlock?.(block)}
          >
            {renderKnownBlock(block, { onFollowUpSelect, onApprovalOptionSelect })}
          </BlockErrorBoundary>
        );
      })}
    </div>
  );
}
